import { Users } from 'lucide-react'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { ResetPasswordDialog } from '~/components/ResetPasswordDialog'
import { PageHeader } from '~/components/PageHeader'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import { RoleDistributionCard } from '~/components/users/RoleDistributionCard'
import { useUsers } from '~/components/users/useUsers'
import { UsersKpiRow } from '~/components/users/UsersKpiRow'
import { UsersTable } from '~/components/users/UsersTable'

// 사용자/조직/권한 (DESIGN.md §5.1 /admin/users).
// 데이터 로드·상태·부수효과는 useUsers 훅으로, 표시는 KPI·역할 분포·표 하위로 분리하고
// 이 컴포넌트는 오케스트레이션 + 레이아웃(스켈레톤/빈 상태 분기 + 확인 모달)만 담당한다.
export function UsersPage() {
    const {
        isSuperadmin,
        loading,
        pending,
        setPending,
        pwdTarget,
        setPwdTarget,
        statusPending,
        setStatusPending,
        query,
        setQuery,
        statusFilter,
        setStatusFilter,
        selection,
        bulkRole,
        setBulkRole,
        bulkConfirm,
        setBulkConfirm,
        bulkBusy,
        confirmRole,
        confirmStatus,
        applyBulkRole,
        total,
        adminCount,
        orgs,
        hasOrg,
        pendingCount,
        roleDist,
        filtered,
        empty,
        visibleIds,
        headState,
    } = useUsers()

    return (
        <div>
            <PageHeader
                title='사용자 / 조직'
                description='권한 변경은 감사 로그를 남겨요.'
            />

            {loading ? (
                <div className='space-y-4'>
                    <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className='h-20' />
                        ))}
                    </div>
                    <Skeleton className='h-48' />
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className='h-9' />
                    ))}
                </div>
            ) : empty ? (
                <EmptyState
                    icon={Users}
                    title='사용자가 없어요.'
                    description='knock SSO로 사용자가 가입하면 여기에서 권한·조직을 관리할 수 있어요.'
                />
            ) : (
                <>
                    {/* 상단 KPI */}
                    <UsersKpiRow
                        total={total}
                        pendingCount={pendingCount}
                        adminCount={adminCount}
                        hasOrg={hasOrg}
                        orgs={orgs}
                    />

                    {/* 역할 분포 */}
                    <RoleDistributionCard
                        roleDist={roleDist}
                        total={total}
                        empty={empty}
                    />

                    <UsersTable
                        query={query}
                        setQuery={setQuery}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        selection={selection}
                        bulkRole={bulkRole}
                        setBulkRole={setBulkRole}
                        bulkBusy={bulkBusy}
                        setBulkConfirm={setBulkConfirm}
                        filtered={filtered}
                        visibleIds={visibleIds}
                        headState={headState}
                        isSuperadmin={isSuperadmin}
                        setStatusPending={setStatusPending}
                        setPending={setPending}
                        setPwdTarget={setPwdTarget}
                    />
                </>
            )}
            <ConfirmDialog
                open={pending !== null}
                title='역할 변경'
                description={
                    pending
                        ? `${pending.user.email} 사용자의 역할을 '${pending.role}'로 변경할까요?`
                        : undefined
                }
                confirmLabel='변경'
                danger
                onConfirm={confirmRole}
                onCancel={() => setPending(null)}
            />
            <ConfirmDialog
                open={statusPending !== null}
                title={
                    statusPending?.status === 'rejected'
                        ? '가입 거부'
                        : '가입 승인'
                }
                description={
                    statusPending
                        ? statusPending.status === 'active'
                            ? `${statusPending.user.email} 사용자의 가입을 승인할까요?`
                            : `${statusPending.user.email} 사용자의 가입을 거부할까요?`
                        : undefined
                }
                confirmLabel={
                    statusPending?.status === 'rejected' ? '거부' : '승인'
                }
                danger={statusPending?.status === 'rejected'}
                onConfirm={confirmStatus}
                onCancel={() => setStatusPending(null)}
            />
            <ConfirmDialog
                open={bulkConfirm}
                title='역할 일괄 변경'
                description={`선택한 ${selection.count}명의 역할을 '${bulkRole}'(으)로 바꿀까요? 권한 변경은 감사 로그를 남겨요.`}
                confirmLabel='변경'
                danger
                onConfirm={applyBulkRole}
                onCancel={() => setBulkConfirm(false)}
            />
            <ResetPasswordDialog
                userId={pwdTarget?.id ?? null}
                email={pwdTarget?.email ?? ''}
                onClose={() => setPwdTarget(null)}
            />
        </div>
    )
}
